import Account from "../accounts/account";
import AccountType from "../accounts/accounts.types";
import { NetworkProvider } from "../api/api";
import Challenge from "../challenge/challenge";
import ChallengeType from "../challenge/challenge.types";
import Session from "../sessions/session";
import SessionType from "../sessions/sessions.types";
import Mfa from "../tokens/mfa";
import MfaType from "../tokens/mfa.types";
import { Organization } from "../types/organization";
import {
  CategoryEnum,
  ChannelConfigEnum,
  ChannelEnum,
  ErrorData,
  FactorsEnum,
  OtpResponse,
  Response,
  ScopeEnum,
  SuccessAuthResponseData,
  SuccessMfaResponseData
} from "../types";
import { getDomainFromEmail } from "../utils/email";
import { AuthConfig } from "./auth.types";

export default class AuthProvider {
  private static instance: AuthProvider;
  private static config: AuthConfig;
  private constructor() {}

  public static getInstance(config?: AuthConfig): AuthProvider {
    if (!AuthProvider.instance) {
      NetworkProvider.getInstance(config?.env);
      AuthProvider.config = config;
      AuthProvider.instance = new AuthProvider();
    }

    return AuthProvider.instance;
  }

  public async reinitialize_login_signup(
    channel: ChannelEnum,
    category: CategoryEnum,
    factor: FactorsEnum,
    challengeId: string,
    config: { [key in ChannelConfigEnum]?: string },
    scope: ScopeEnum | string
  ): Promise<OtpResponse> {
    const result = await this.get_challenge(
      channel,
      category,
      factor,
      config,
      scope,
      challengeId
    );
    if (result.success) {
      const response: OtpResponse = {
        success: true,
        data: {
          id: result.challenge,
          is_new_user: null,
          expiry: result.expiry
        }
      };
      return response;
    } else {
      const response: OtpResponse = {
        success: false,
        errors: result?.errors as ErrorData[]
      };
      return response;
    }
  }

  public async get_factors(
    channel: ChannelEnum,
    category: CategoryEnum,
    domain: string,
    factor: FactorsEnum,
    config: { [key in ChannelConfigEnum]?: string }
  ) {
    let organization = this.getOrganization(config.email);

    if (!organization) {
      organization = { domain: domain };
    }

    const session = new Session();

    const sessionData: SessionType = {
      category: category,
      organization,
      config: config
    };

    const result = await session.createSession(
      channel,
      factor,
      sessionData,
      ""
    );
    return result;
  }

  public async get_challenge(
    channel: ChannelEnum,
    category: CategoryEnum,
    factor: FactorsEnum,
    config: { [key in ChannelConfigEnum]?: string },
    scope: ScopeEnum | string,
    challengeId?: string,
    xAuth?: string
  ): Promise<{
    success: boolean;
    exists?: boolean;
    expiry?: Record<string, string>;
    errors?: ErrorData[];
    challenge?: string;
  }> {
    const organization = this.getOrganization(config.email);
    const challenge = new Challenge();
    if (challengeId) {
      const challengeConfig: ChallengeType = xAuth
        ? {
            scope: scope,
            channel: channel
          }
        : {
            channel: channel,
            category: category,
            organization,
            scope: scope,
            config: config
          };
      const result = xAuth
        ? await challenge.createNewChallengeAuthenticated(
            factor,
            challengeConfig,
            challengeId,
            xAuth
          )
        : await challenge.createNewChallengeUnAuthenticated(
            factor,
            challengeConfig,
            challengeId
          );
      return result;
    } else {
      const challengeConfig: ChallengeType = xAuth
        ? {
            scope: scope,
            channel: channel,
            ...(scope === ScopeEnum.EmailMerge || scope === ScopeEnum.PhoneMerge
              ? { config }
              : {})
          }
        : {
            channel: channel,
            category: category,
            organization,
            scope: scope,
            config: config
          };
      const result = xAuth
        ? await challenge.createChallengeAuthenticated(
            factor,
            challengeConfig,
            xAuth
          )
        : await challenge.createChallengeUnAuthenticated(
            factor,
            challengeConfig
          );
      return result;
    }
  }

  public async initialize_login_signup(
    channel: ChannelEnum,
    category: CategoryEnum,
    factor: FactorsEnum,
    config: { [key in ChannelConfigEnum]?: string },
    scope: ScopeEnum | string
  ): Promise<OtpResponse> {
    const organization = this.getOrganization(config.email);
    let result = await this.get_challenge(
      channel,
      category,
      factor,
      config,
      scope
    );

    if (!result.success && result?.errors?.[0]?.code === 1001) {
      const response: OtpResponse = {
        success: false,
        errors: result?.errors as ErrorData[]
      };
      return response;
    }

    let factors: any = {};
    if (!result?.exists) {
      const account = new Account();
      const accountConfig: AccountType = {
        category: category,
        organization,
        config: config
      };
      result = await account.createAccountChallenge(
        channel,
        factor,
        accountConfig
      );
    } else {
      const factorResult = await this.get_factors(
        channel,
        category,
        (organization as any).domain ?? "",
        factor,
        config
      );
      factors = factorResult.data;
    }

    if (result.success) {
      const response: OtpResponse = {
        success: true,
        data: {
          id: result.challenge,
          is_new_user: !result.exists,
          factors: factors,
          expiry: result.expiry
        }
      };
      return response;
    } else {
      const response: OtpResponse = {
        success: false,
        errors: result?.errors as ErrorData[]
      };
      return response;
    }
  }

  public async get_mfa_token(
    scope: ScopeEnum | string,
    factor: FactorsEnum,
    challenge: {
      id: string;
      code?: string;
      secret?: string;
      password?: string;
    },
    config?: { [key in ChannelConfigEnum]?: string },
    channel?: ChannelEnum,
    category?: CategoryEnum,
    xMfa?: string,
    xAuth?: string
  ): Promise<Response> {
    const mfa = new Mfa();
    const organization = this.getOrganization(config?.email);
    const mfaData: MfaType = xAuth
      ? {
          type: factor,
          channel: channel,
          scope: scope,
          challenge: challenge
        }
      : {
          category: category,
          organization,
          type: factor,
          channel: channel,
          scope: scope,
          config: config,
          challenge: challenge
        };
    const mfaResult = xAuth
      ? await mfa.createMfaTokenAuthenticated(mfaData, xAuth, xMfa)
      : await mfa.createMfaTokenUnAuthenticated(mfaData, xMfa);
    if (mfaResult.success) {
      return {
        success: true,
        data: mfaResult.mfaToken
      };
    } else {
      return {
        success: false,
        errors: mfaResult.error
      };
    }
  }

  public async login_signup(
    channel: ChannelEnum,
    category: CategoryEnum,
    factor: FactorsEnum,
    config: { [key in ChannelConfigEnum]?: string },
    is_new_user: boolean,
    mfaToken: string,
    meta: {
      [key: string]: any;
    }
  ): Promise<Response> {
    const organization = this.getOrganization(config.email);
    if (is_new_user) {
      const account = new Account();
      const accountData: AccountType = {
        category: category,
        organization,
        config: config,
        meta: meta
      };
      if (factor === FactorsEnum.OTP && config.password) {
        factor = FactorsEnum.Password;
      }
      const result = await account.verifyAccountChallenge(
        channel,
        factor,
        accountData,
        mfaToken
      );
      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          errors: result.errors
        };
      }
    } else {
      const session = new Session();
      const sessionData: SessionType = {
        category: category,
        organization,
        config: config
      };
      const result = await session.createSession(
        channel,
        factor,
        sessionData,
        mfaToken
      );

      if (result.success) {
        return {
          success: true,
          data: result.data
        };
      } else {
        return {
          success: false,
          errors: result.errors
        };
      }
    }
  }

  public async mergeAccount(
    channel: ChannelEnum,
    scope: ScopeEnum,
    factor: FactorsEnum,
    xAuth: string,
    challenge: {
      id: string;
      code?: string;
    },
    configData: { [key in ChannelConfigEnum]?: string }
  ) {
    try {
      const createMfaResponse = await this.get_mfa_token(
        scope,
        factor,
        { id: challenge?.id, code: challenge?.code },
        {},
        channel,
        null,
        "",
        xAuth
      );
      if (createMfaResponse?.success) {
        const account = new Account();
        const mergeAccountResponse = await account?.mergeAccountChallenge(
          channel,
          (createMfaResponse?.data as SuccessMfaResponseData)?.access_token,
          scope,
          configData,
          xAuth
        );
        return mergeAccountResponse;
      } else {
        return {
          success: false,
          errors: createMfaResponse?.errors as ErrorData[]
        };
      }
    } catch (e) {}
  }

  getOrganization(email?: string): Organization {
    if (AuthProvider.config?.organization) {
      return AuthProvider.config?.organization;
    }

    if (AuthProvider.config?.domain) {
      return { domain: AuthProvider.config?.domain };
    }

    return { domain: getDomainFromEmail(email ?? "") };
  }
}
